/**
 * WorkOrderControlTab
 * -------------------
 * The Work Control Center — the maintenance twin of the backorder Fulfillment
 * Control Center. Same shape: PO banner, vendor PO buttons, notification
 * recipients, milestone tiles, milestone form, posted timeline.
 *
 * Milestones: acknowledged → scheduled → dispatched → on site → (delayed) →
 * completed → signed off. Each one flips the work-order status and emails the
 * hospital.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Mail, CalendarClock, Truck, HardHat, AlertTriangle, CheckCircle2,
  FileSignature, FileText, Wrench, Package,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import FulfillmentRecipientsEditor from "@/components/admin/FulfillmentRecipientsEditor";
import WorkOrderPoEmailDialog from "@/components/admin/WorkOrderPoEmailDialog";
import WorkOrderPartsPanel from "@/components/admin/WorkOrderPartsPanel";

const API = process.env.REACT_APP_BACKEND_URL;

const MILESTONES = [
  { id: "scheduled", label: "Scheduled", hint: "Date / time + crew", icon: CalendarClock,
    cls: "border-sky-200 bg-sky-50 hover:bg-sky-100", text: "text-sky-800" },
  { id: "dispatched", label: "Crew Dispatched", hint: "On the way to site", icon: Truck,
    cls: "border-violet-200 bg-violet-50 hover:bg-violet-100", text: "text-violet-800" },
  { id: "on_site", label: "On Site / Started", hint: "Work underway", icon: HardHat,
    cls: "border-cyan-200 bg-cyan-50 hover:bg-cyan-100", text: "text-cyan-800" },
  { id: "delayed", label: "Delayed", hint: "New date + reason", icon: AlertTriangle,
    cls: "border-amber-200 bg-amber-50 hover:bg-amber-100", text: "text-amber-800" },
  { id: "completed", label: "Work Completed", hint: "Ready for sign-off", icon: CheckCircle2,
    cls: "border-emerald-200 bg-emerald-50 hover:bg-emerald-100", text: "text-emerald-800" },
  { id: "signed_off", label: "Signed Off & Closed", hint: "Hospital accepted", icon: FileSignature,
    cls: "border-emerald-300 bg-emerald-100 hover:bg-emerald-200", text: "text-emerald-900" },
];

const META = Object.fromEntries(MILESTONES.map((m) => [m.id, m]));

export default function WorkOrderControlTab({ workOrder, token, onRefresh }) {
  const headers = { Authorization: `Bearer ${token}` };
  const woId = workOrder?.id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);
  const [form, setForm] = useState({});
  const [posting, setPosting] = useState(false);
  const [sendingAck, setSendingAck] = useState(false);
  const [poDialog, setPoDialog] = useState(null); // "contractor" | "supplier"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/work-orders/${woId}/milestones`, { headers });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the control center");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [woId, token]);
  useEffect(() => { load(); }, [load]);

  const poNumber = (data?.customer_order?.po_number || workOrder?.po_number || "").trim();
  const ackSentAt = data?.po_acknowledgment_sent_at;

  const open = (id) => {
    setDrawer(id);
    setForm({
      scheduled_for: id === "scheduled" || id === "delayed" ? (data?.scheduled_at || "") : "",
      window: "", crew: "", reason: "", note: "",
      contractor_name: workOrder?.awarded_contractor_name || "",
      work_title: id === "on_site"
        ? (workOrder?.work_title
           || workOrder?.description
           || workOrder?.items?.[0]?.description
           || "")
        : "",
      completion_summary: id === "completed" ? (workOrder?.completion_summary || "") : "",
    });
  };

  const postMilestone = async () => {
    setPosting(true);
    try {
      const r = await axios.post(`${API}/api/admin/work-orders/${woId}/milestones`,
        { type: drawer, po_number: poNumber || null, ...form }, { headers });
      toast.success(`${META[drawer].label} posted`, {
        description: r.data?.emailed
          ? "The hospital has been emailed and the work order status updated."
          : "Status updated — no hospital recipient was emailed (check Notification Recipients).",
      });
      setDrawer(null);
      await load();
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post the milestone");
    } finally {
      setPosting(false);
    }
  };

  const sendAck = async () => {
    if (!poNumber) return toast.error("Save a hospital PO number on the Build Order tab first.");
    setSendingAck(true);
    try {
      const r = await axios.post(`${API}/api/admin/work-orders/${woId}/email-po-acknowledgment`,
        { po_number: poNumber }, { headers });
      toast.success("Work order acknowledgment sent", {
        description: `To: ${(r.data?.recipients?.to || []).join(", ") || "—"}`,
      });
      await load();
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the acknowledgment");
    } finally {
      setSendingAck(false);
    }
  };

  const milestones = (data?.milestones || []).slice().reverse();

  return (
    <div className="space-y-5" data-testid="wo-control-center-tab">
      {/* Top action bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
        <div>
          <h3 className="text-sm font-bold text-slate-800 inline-flex items-center gap-2">
            <Wrench className="w-4 h-4 text-slate-600" />
            Work Control Center
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Post each milestone as it happens &mdash; the hospital is emailed, the work-order status
            flips, and the timeline below records who did what and when.
          </p>
          <p className="text-xs mt-1" data-testid="wo-control-po-line">
            {poNumber ? (
              <span className="font-bold text-indigo-800 bg-indigo-50 border border-indigo-200 rounded px-2 py-0.5">
                PO #{poNumber}
              </span>
            ) : (
              <span className="text-amber-700">
                No hospital PO yet — add it in the <strong>Build Order</strong> tab so it appears on
                every email.
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 self-start sm:self-auto">
          <Button variant="outline" size="sm" onClick={() => setPoDialog("contractor")}
            className="border-indigo-300 text-indigo-800 hover:bg-indigo-50"
            data-testid="wo-contractor-po-email-btn">
            <FileText className="w-3.5 h-3.5 mr-1.5" /> Send Work Order PO to Contractor
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPoDialog("supplier")}
            className="border-amber-300 text-amber-800 hover:bg-amber-50"
            data-testid="wo-supplier-po-email-btn">
            <Package className="w-3.5 h-3.5 mr-1.5" /> Send Parts PO to Supplier
          </Button>
        </div>
      </div>

      {poDialog && (
        <WorkOrderPoEmailDialog
          open={!!poDialog}
          onOpenChange={(v) => !v && setPoDialog(null)}
          workOrderId={woId}
          kind={poDialog}
          token={token}
          onLogged={onRefresh}
        />
      )}

      <FulfillmentRecipientsEditor
        baseUrl={`${API}/api/admin/work-orders/${woId}`}
        token={token}
        onSaved={onRefresh}
        blurb="Every work-order email (acknowledgment, scheduled, dispatched, on site, delayed, completed, signed off) goes to these addresses."
      />

      <WorkOrderPartsPanel workOrderId={woId} token={token} onChanged={onRefresh} />

      {/* Milestone tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <button type="button" onClick={sendAck} disabled={!poNumber || sendingAck}
          className="p-4 rounded-lg border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 transition-all text-left relative disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="wo-milestone-ack-btn"
          title={!poNumber ? "Add a hospital PO on the Build Order tab to unlock this."
            : ackSentAt ? `Last sent ${new Date(ackSentAt).toLocaleString()} · click to re-send`
              : "Send the 'we have your work order' email to the hospital."}>
          {sendingAck ? <Loader2 className="w-5 h-5 text-blue-700 mb-2 animate-spin" />
            : <Mail className="w-5 h-5 text-blue-700 mb-2" />}
          <div className="font-bold text-blue-800 text-sm">
            {ackSentAt ? "Re-send Acknowledgment" : "Work Order Acknowledged"}
          </div>
          <div className="text-xs text-blue-700 mt-0.5">
            {poNumber ? "\u201cWe have your work order\u201d email" : "PO required — add it in Build Order"}
          </div>
          {ackSentAt && !sendingAck && (
            <span className="absolute top-2 right-2 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-1.5 py-0.5">
              <CheckCircle2 className="w-2.5 h-2.5" /> Sent
            </span>
          )}
        </button>

        {MILESTONES.map((m) => (
          <button key={m.id} type="button" onClick={() => open(m.id)}
            className={`p-4 rounded-lg border-2 ${m.cls} transition-all text-left`}
            data-testid={`wo-milestone-${m.id}-btn`}>
            <m.icon className={`w-5 h-5 ${m.text} mb-2`} />
            <div className={`font-bold ${m.text} text-sm`}>{m.label}</div>
            <div className={`text-xs ${m.text} mt-0.5 opacity-80`}>{m.hint}</div>
          </button>
        ))}
      </div>

      {/* Milestone form */}
      {drawer && (
        <div className={`rounded-lg border-2 ${META[drawer].cls} p-4 space-y-3`}
          data-testid={`wo-milestone-form-${drawer}`}>
          <div className="flex items-center justify-between">
            <h4 className={`text-sm font-bold ${META[drawer].text}`}>
              {META[drawer].label} milestone
            </h4>
            <button onClick={() => setDrawer(null)} className="text-xs text-slate-500 hover:text-slate-700">
              Cancel
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {drawer === "on_site" && (
              <div className="sm:col-span-2">
                <Field label="Work being performed *">
                  <Input
                    value={form.work_title || ""}
                    onChange={(e) => setForm((p) => ({ ...p, work_title: e.target.value }))}
                    placeholder="Replace pressure relief valve — OR-3 scrub sink"
                    data-testid="wo-milestone-work-title"
                  />
                </Field>
                <p className="text-[11px] text-slate-500 mt-1">
                  This title heads the hospital&rsquo;s &ldquo;work has started&rdquo; email and the
                  work order timeline.
                </p>
              </div>
            )}
            {drawer === "completed" && (
              <div className="sm:col-span-2">
                <Field label="What we did — short summary for the hospital *">
                  <Textarea
                    rows={3}
                    value={form.completion_summary || ""}
                    onChange={(e) => setForm((p) => ({ ...p, completion_summary: e.target.value }))}
                    placeholder="Replaced the failed pressure relief valve and reseated the mixing cartridge. Ran the sink 15 minutes — no drip, temperature holding at 110°F. Area cleaned and returned to service."
                    data-testid="wo-milestone-completion-summary"
                  />
                </Field>
                <p className="text-[11px] text-slate-500 mt-1">
                  This appears as a green <strong>&ldquo;What we did&rdquo;</strong> panel at the top
                  of the hospital&rsquo;s completion email and on the work order timeline.
                </p>
              </div>
            )}
            {(drawer === "scheduled" || drawer === "delayed") && (
              <Field label={drawer === "delayed" ? "New service date *" : "Service date *"}>
                <Input value={form.scheduled_for || ""}
                  onChange={(e) => setForm((p) => ({ ...p, scheduled_for: e.target.value }))}
                  placeholder="Tue, Oct 21 2026" data-testid="wo-milestone-date" />
              </Field>
            )}
            {(drawer === "scheduled" || drawer === "dispatched" || drawer === "delayed") && (
              <Field label="Arrival window">
                <Input value={form.window || ""}
                  onChange={(e) => setForm((p) => ({ ...p, window: e.target.value }))}
                  placeholder="7:00 – 10:00 AM" data-testid="wo-milestone-window" />
              </Field>
            )}
            <Field label="Service partner">
              <Input value={form.contractor_name || ""}
                onChange={(e) => setForm((p) => ({ ...p, contractor_name: e.target.value }))}
                data-testid="wo-milestone-contractor" />
            </Field>
            <Field label="Crew / technician">
              <Input value={form.crew || ""}
                onChange={(e) => setForm((p) => ({ ...p, crew: e.target.value }))}
                placeholder="2 techs · Ruben G." data-testid="wo-milestone-crew" />
            </Field>
            {drawer === "delayed" && (
              <Field label="Reason for the delay *">
                <Input value={form.reason || ""}
                  onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
                  placeholder="Part on backorder from OEM" data-testid="wo-milestone-reason" />
              </Field>
            )}
          </div>

          <Field label="Note to the hospital (optional)">
            <Textarea rows={2} value={form.note || ""}
              onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
              data-testid="wo-milestone-note" />
          </Field>

          <Button onClick={postMilestone} disabled={posting}
            className="bg-slate-900 hover:bg-slate-800" data-testid="wo-milestone-submit-btn">
            {posting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
            Post milestone &amp; email hospital
          </Button>
        </div>
      )}

      {/* Timeline */}
      <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="wo-milestone-timeline">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
          Posted milestones
        </h4>
        {loading ? (
          <div className="text-xs text-slate-400 inline-flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        ) : milestones.length === 0 ? (
          <p className="text-sm text-slate-500">
            Nothing posted yet — start with the acknowledgment, then schedule the visit.
          </p>
        ) : (
          <ul className="space-y-2">
            {milestones.map((m) => (
              <li key={m.id} className="border-l-2 border-slate-300 pl-3 py-1"
                data-testid={`wo-milestone-row-${m.type}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-slate-800">
                    {META[m.type]?.label || (m.type === "acknowledged" ? "Work Order Acknowledged" : m.type)}
                  </span>
                  <span className="text-[11px] text-slate-400">
                    {m.ts ? new Date(m.ts).toLocaleString() : ""} · {m.by}
                  </span>
                </div>
                <p className="text-xs text-slate-600">
                  {[m.completion_summary, m.work_title, m.scheduled_for && `Scheduled ${m.scheduled_for}`, m.window,
                    m.crew, m.contractor_name, m.reason, m.note].filter(Boolean).join(" · ")}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
      {label}
    </label>
    {children}
  </div>
);
